import React, { useState, useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { grabService, GrabIntegrationConfig } from '../services/grabService';
import { useToast } from '../hooks/useToast';
import { useCurrencySettingsStore } from '../stores/currencySettingsStore';

// shadcn/ui components
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Info, CheckCircle2, XCircle, RefreshCw } from 'lucide-react';

export const ModernGrabIntegrationSettings: React.FC = () => {
  const { translate } = useTranslation();
  const { success, error: showError } = useToast();
  const { settings: currencySettings } = useCurrencySettingsStore();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState<GrabIntegrationConfig | null>(null);
  const [formData, setFormData] = useState({
    environment: 'sandbox' as 'sandbox' | 'staging' | 'production',
    clientId: '',
    clientSecret: '',
    partnerId: '',
    partnerMerchantId: '',
    webhookUrl: '',
    storeId: '',
    isActive: false,
  });

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const activeConfig = await grabService.getActiveConfig();
      if (activeConfig) {
        setConfig(activeConfig);
        setFormData({
          environment: activeConfig.environment,
          clientId: activeConfig.clientId,
          clientSecret: '', // Don't show secret
          partnerId: activeConfig.partnerId,
          partnerMerchantId: activeConfig.partnerMerchantId,
          webhookUrl: activeConfig.webhookUrl,
          storeId: activeConfig.storeId || '',
          isActive: activeConfig.isActive,
        });
      }
    } catch (error: any) {
      console.error('Failed to load config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Use system currency settings
      const configData = {
        type: 'grab' as const,
        environment: formData.environment,
        clientId: formData.clientId,
        clientSecret: formData.clientSecret,
        partnerId: formData.partnerId,
        partnerMerchantId: formData.partnerMerchantId,
        webhookUrl: formData.webhookUrl,
        storeId: formData.storeId || undefined,
        currency: currencySettings.text, // Use system currency code
        currencyExponent: currencySettings.precision, // Use system precision
        isActive: formData.isActive,
      };

      if (config) {
        await grabService.updateConfig(config.id, configData);
        success('Configuration updated successfully');
      } else {
        await grabService.createConfig(configData);
        success('Configuration created successfully');
      }

      await loadConfig();
    } catch (error: any) {
      showError(error.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      // Test by trying to get access token
      success('Connection test feature coming soon');
    } catch (error: any) {
      showError(error.message || 'Connection test failed');
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Grab Integration Settings</h2>
        <p className="text-muted-foreground">
          Configure your GrabFood API integration
        </p>
      </div>

      {config && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            {config.isActive ? (
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                <span>Integration is active</span>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <XCircle className="w-4 h-4 text-red-500" />
                <span>Integration is inactive</span>
              </div>
            )}
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>API Configuration</CardTitle>
          <CardDescription>
            Enter your GrabFood API credentials and configuration
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="environment">Environment *</Label>
                <Select
                  value={formData.environment}
                  onValueChange={(value: any) =>
                    setFormData({ ...formData, environment: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sandbox">Sandbox</SelectItem>
                    <SelectItem value="staging">Staging</SelectItem>
                    <SelectItem value="production">Production</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="isActive">Status</Label>
                <div className="flex items-center space-x-2 pt-2">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={formData.isActive}
                    onChange={(e) =>
                      setFormData({ ...formData, isActive: e.target.checked })
                    }
                    className="rounded"
                  />
                  <Label htmlFor="isActive" className="cursor-pointer">
                    Active
                  </Label>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="clientId">Client ID *</Label>
              <Input
                id="clientId"
                value={formData.clientId}
                onChange={(e) =>
                  setFormData({ ...formData, clientId: e.target.value })
                }
                placeholder="Enter Grab client ID"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="clientSecret">
                Client Secret {config ? '(leave blank to keep current)' : '*'}
              </Label>
              <Input
                id="clientSecret"
                type="password"
                value={formData.clientSecret}
                onChange={(e) =>
                  setFormData({ ...formData, clientSecret: e.target.value })
                }
                placeholder="Enter Grab client secret"
                required={!config}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="partnerId">Partner ID *</Label>
              <Input
                id="partnerId"
                value={formData.partnerId}
                onChange={(e) =>
                  setFormData({ ...formData, partnerId: e.target.value })
                }
                placeholder="Enter Grab partner ID"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="partnerMerchantId">Partner Merchant ID *</Label>
              <Input
                id="partnerMerchantId"
                value={formData.partnerMerchantId}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    partnerMerchantId: e.target.value,
                  })
                }
                placeholder="Enter Grab partner merchant ID"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="webhookUrl">Webhook URL *</Label>
              <Input
                id="webhookUrl"
                value={formData.webhookUrl}
                onChange={(e) =>
                  setFormData({ ...formData, webhookUrl: e.target.value })
                }
                placeholder="https://your-domain.com/webhooks/grab"
                required
              />
              <p className="text-xs text-muted-foreground">
                Configure this URL in your Grab dashboard
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="storeId">Store ID</Label>
              <Input
                id="storeId"
                value={formData.storeId}
                onChange={(e) =>
                  setFormData({ ...formData, storeId: e.target.value })
                }
                placeholder="Enter Grab store ID (optional)"
              />
            </div>

            <div className="space-y-2">
              <Label>Currency Settings</Label>
              <div className="p-4 bg-muted rounded-md">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">
                      Using system currency: <span className="font-bold">{currencySettings.text}</span>
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Currency exponent: {currencySettings.precision} (from system settings)
                    </p>
                  </div>
                  <Badge variant="outline">
                    Auto-synced
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Currency settings are automatically synchronized with your system currency configuration.
                  To change currency, update it in Settings → Currency.
                </p>
              </div>
            </div>

            <div className="flex gap-2">
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving...' : 'Save Configuration'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleTestConnection}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Test Connection
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

